import { CoreApplicationContext } from '@eventbrite/context-gen';
import { getWindowObject } from '@eventbrite/feature-detection';
import { getLocaleOverride, getParentLocale } from '@eventbrite/i18n';
import omit from 'lodash/omit';
import statsig, { StatsigOptions, StatsigUser } from 'statsig-js';
import {
    DEFAULT_SAMPLE_RATE,
    DEV_ENV_URL_REGEX,
    PROD_ENV_URLS_REGEX,
    QA_ENV_URLS_REGEX,
    STATSIG_ENV,
    STATSIG_OVERRIDE_PARAM,
} from '../constants/constants';
import { StatsigEnvironments } from '../models/statsigEnvironments';

const ensureSDKIsInitialized = (fn: () => any, defaultValue: any = null) =>
    isStatsigInitialized() ? fn() : defaultValue;

const addValueIfExists = <T>(key: string, value: T) =>
    value ? { [key]: value } : {};

const getUser = (userOptions?: StatsigUser) => {
    const { app_version, env, request, user }: CoreApplicationContext =
        getWindowObject('__SERVER_DATA__') ?? {};
    const { userAgent } = getWindowObject('navigator') ?? {};

    const publicUserOptions: StatsigUser = {
        ...addValueIfExists('userID', userOptions?.userID),
        ...addValueIfExists('ip', userOptions?.ip),
        ...addValueIfExists('country', userOptions?.country),
        ...addValueIfExists('locale', userOptions?.locale),
        ...addValueIfExists('appVersion', userOptions?.appVersion),
        ...addValueIfExists('custom', userOptions?.custom),
        ...addValueIfExists(
            'privateAttributes',
            userOptions?.privateAttributes,
        ),
        ...addValueIfExists('customIDs', userOptions?.customIDs),
    };

    return {
        locale: getParentLocale(getLocaleOverride() || env?.localeInfo?.locale),
        ...(app_version ? { appVersion: app_version } : {}),
        customIDs: {
            ...(request?.correlation_id
                ? { correlationId: request?.correlation_id }
                : {}),
            ...(request?.session_id ? { sessionId: request?.session_id } : {}),
        },
        ...(user?.publicId ? { userID: user?.publicId } : {}),
        ...(userAgent ? { userAgent } : {}),
        ...publicUserOptions,
        // Anything not explicity set at the top level should be private for PII reasons
        privateAttributes: {
            ...(userOptions?.privateAttributes ?? {}),
            ...omit(userOptions ?? {}, ...Object.keys(publicUserOptions ?? {})),
        },
    };
};

const getStatsigEnv = (): StatsigEnvironments => {
    const { host } = getWindowObject('location') ?? {};

    if (PROD_ENV_URLS_REGEX.test(host)) {
        return STATSIG_ENV.PROD;
    }
    if (QA_ENV_URLS_REGEX.test(host)) {
        return STATSIG_ENV.QA;
    }
    if (DEV_ENV_URL_REGEX.test(host)) {
        return STATSIG_ENV.DEV;
    }
    return;
};

const getOptions = (options?: StatsigOptions) => ({
    environment: {
        tier: getStatsigEnv(),
    },
    ...options,
});

const getUrlParams = () => {
    const { request }: CoreApplicationContext =
        getWindowObject('__SERVER_DATA__') ?? {};
    return request?.params ?? null;
};

const getExperimentValueToOverride = (
    experimentName: string,
): string | null => {
    const urlParams = getUrlParams();

    if (!urlParams || getStatsigEnv() === STATSIG_ENV.PROD) {
        return null;
    }

    const [experimentToOverride, valueToOverride] =
        urlParams?.[STATSIG_OVERRIDE_PARAM]?.split(':') || [];

    return experimentToOverride === experimentName ? valueToOverride : null;
};

const isStatsigInitialized = () => !!(statsig as any).instance;

const shouldInitializeSDK = (
    sampleRate: number = DEFAULT_SAMPLE_RATE,
    deactivateSdk?: boolean,
) => !deactivateSdk && Math.random() * 100 < sampleRate;

export {
    ensureSDKIsInitialized,
    getExperimentValueToOverride,
    getOptions,
    getUrlParams,
    getUser,
    isStatsigInitialized,
    shouldInitializeSDK,
};
